// src/pages/dashboard/DashboardAdmin.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Building2, FileText, Database, Settings, Activity, Shield, Server, HardDrive, Cpu, Clock, AlertTriangle, TrendingUp, CheckCircle, BarChart3, ArrowUpRight, RefreshCw, Eye } from 'lucide-react';
import { Card, Badge, Breadcrumb, ProgressBar, Loading } from '../../components/ui';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts';
import api from '../../services/api';

const DashboardAdmin: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [systemStats, setSystemStats] = useState({ total_users: 0, active_users: 0, total_units: 0, total_laporan: 0, total_rapim: 0, total_ppepp: 0, storage_used: 0, storage_total: 10, uptime: '99.8%', avg_response: '245ms' });
  const [usersByRole, setUsersByRole] = useState<any[]>([]);
  const [recentLogs, setRecentLogs] = useState<any[]>([]);

  const fetchAllUsers = async () => {
    const perPage = 200;
    let page = 1;
    let lastPage = 1;
    const allRows: any[] = [];

    do {
      const res = await api.get('/admin/users', { params: { page, per_page: perPage } });
      const paginator = res.data?.data || {};
      const rows = Array.isArray(paginator?.data) ? paginator.data : [];
      allRows.push(...rows);
      lastPage = Number(paginator?.last_page || 1);
      page += 1;
    } while (page <= lastPage);

    return allRows;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dashRes, usersRes, activeUsersRes, logsRes, unitsRes] = await Promise.all([
          api.get('/dashboard'),
          api.get('/admin/users', { params: { page: 1, per_page: 1 } }).catch(() => ({ data: { data: { data: [], total: 0 } } })),
          api.get('/admin/users', { params: { page: 1, per_page: 1, is_active: 1 } }).catch(() => ({ data: { data: { data: [], total: 0 } } })),
          api.get('/admin/logs').catch(() => ({ data: { data: [] } })),
          api.get('/admin/units', { params: { page: 1, per_page: 1 } }).catch(() => ({ data: { data: { data: [], total: 0 } } })),
        ]);
        const d = dashRes.data?.data || dashRes.data;
        if (d) {
          setSystemStats(prev => ({ ...prev, total_laporan: d.total_laporan || 0, total_rapim: d.total_rapim || 0, total_ppepp: d.total_ppepp || 0 }));
        }
        const usersPaginator = usersRes.data?.data || {};
        const activeUsersPaginator = activeUsersRes.data?.data || {};
        const unitsPaginator = unitsRes.data?.data || {};
        const totalUsers = Number(usersPaginator?.total || 0);
        const totalActiveUsers = Number(activeUsersPaginator?.total || 0);
        const totalUnits = Number(unitsPaginator?.total || 0);
        setSystemStats(prev => ({ ...prev, total_users: totalUsers, active_users: totalActiveUsers, total_units: totalUnits }));

        const usersArr = await fetchAllUsers();
        const roleMap: Record<string, number> = {};
        usersArr.forEach((u: any) => { const r = u.role?.name || u.role || 'Other'; roleMap[r] = (roleMap[r] || 0) + 1; });
        setUsersByRole(Object.entries(roleMap).map(([role, count]) => ({ role, count })));
        const logs = logsRes.data?.data || [];
        setRecentLogs(Array.isArray(logs) ? logs.slice(0, 8) : []);
      } catch (err) { console.error('Failed:', err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const quickLinks = [
    { label: 'Kelola User', icon: <Users className="w-5 h-5" />, href: '/admin/users', color: 'bg-teal-main/10 text-teal-main' },
    { label: 'Unit Organisasi', icon: <Building2 className="w-5 h-5" />, href: '/admin/units', color: 'bg-info/10 text-info' },
    { label: 'Master Data', icon: <Database className="w-5 h-5" />, href: '/admin/master', color: 'bg-purple-100 text-purple-600' },
    { label: 'Standar SPMI', icon: <Shield className="w-5 h-5" />, href: '/admin/standar', color: 'bg-success/10 text-success' },
    { label: 'Pengaturan', icon: <Settings className="w-5 h-5" />, href: '/admin/settings', color: 'bg-warning/10 text-warning' },
    { label: 'Log Aktivitas', icon: <Activity className="w-5 h-5" />, href: '/admin/logs', color: 'bg-danger/10 text-danger' },
  ];

  const logTypeColors: Record<string, string> = { create: 'bg-success/10 text-success', approve: 'bg-teal-main/10 text-teal-main', update: 'bg-info/10 text-info', system: 'bg-neutral-bg text-neutral-text-secondary', delete: 'bg-danger/10 text-danger' };

  if (loading) return <Loading variant="page" message="Memuat data dashboard..." />;

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard' }, { label: 'Administrator' }]} />
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-black text-teal-dark tracking-tight">Dashboard Administrator</h1><p className="text-sm text-neutral-text-secondary mt-1"><Settings className="w-4 h-4 inline mr-1" />Kelola sistem dan monitor aktivitas</p></div>
        <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-neutral-border text-sm font-medium rounded-lg hover:bg-neutral-bg transition-all"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { title: 'Total User', value: systemStats.total_users, sub: `${systemStats.active_users || systemStats.total_users} aktif`, icon: <Users className="w-5 h-5" />, color: 'text-teal-main', bg: 'bg-teal-main/10' },
          { title: 'Total Laporan', value: systemStats.total_laporan, icon: <FileText className="w-5 h-5" />, color: 'text-info', bg: 'bg-info/10' },
          { title: 'Unit Organisasi', value: systemStats.total_units, icon: <Building2 className="w-5 h-5" />, color: 'text-purple-600', bg: 'bg-purple-100' },
          { title: 'Uptime', value: systemStats.uptime, sub: systemStats.avg_response, icon: <Server className="w-5 h-5" />, color: 'text-success', bg: 'bg-success/10' },
          { title: 'Storage', value: `${systemStats.storage_used} GB`, sub: `/ ${systemStats.storage_total} GB`, icon: <HardDrive className="w-5 h-5" />, color: 'text-warning', bg: 'bg-warning/10' },
        ].map((stat, i) => (
          <Card key={i}><div className="flex items-start justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-widest text-neutral-text-secondary">{stat.title}</p><p className="text-xl font-black mt-1">{stat.value}</p>{stat.sub && <p className="text-xs text-neutral-text-secondary mt-0.5">{stat.sub}</p>}</div><div className={`p-2 rounded-lg ${stat.bg} ${stat.color}`}>{stat.icon}</div></div></Card>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {quickLinks.map((link, i) => (
          <Link key={i} to={link.href}><Card hover padding="sm" className="text-center"><div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${link.color} mx-auto mb-2`}>{link.icon}</div><p className="text-xs font-semibold text-neutral-text-primary">{link.label}</p></Card></Link>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2"><h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2"><Activity className="w-5 h-5" /> Aktivitas Terkini</h3>
          <div className="space-y-2">{recentLogs.length > 0 ? recentLogs.map((log: any, i: number) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-neutral-border last:border-0">
              <span className="text-xs font-mono text-neutral-text-secondary w-16 shrink-0">{log.created_at ? new Date(log.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : log.time || ''}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold shrink-0 ${logTypeColors[log.action_type || log.type || 'system'] || logTypeColors.system}`}>{(log.action_type || log.type || 'SYSTEM').toUpperCase()}</span>
              <div className="flex-1 min-w-0"><p className="text-sm truncate"><span className="font-medium">{log.user?.name || log.user || ''}</span> <span className="text-neutral-text-secondary">— {log.description || log.action || ''}</span></p></div>
            </div>
          )) : <p className="text-sm text-neutral-text-secondary text-center py-4">Belum ada log aktivitas</p>}</div>
        </Card>
        <Card><h3 className="text-base font-bold text-teal-dark mb-4 flex items-center gap-2"><Shield className="w-5 h-5" /> User per Role</h3>
          <div className="space-y-2">{usersByRole.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-1.5"><span className="text-sm">{item.role}</span><div className="flex items-center gap-2"><div className="w-20 bg-gray-200 rounded-full h-1.5"><div className="bg-teal-light h-1.5 rounded-full" style={{ width: `${Math.min((item.count / Math.max(systemStats.total_users, 1)) * 100 * 5, 100)}%` }} /></div><span className="text-sm font-bold w-6 text-right">{item.count}</span></div></div>
          ))}</div>
          <div className="mt-3 pt-3 border-t border-neutral-border"><div className="flex items-center justify-between"><span className="text-xs font-semibold text-neutral-text-secondary">Total</span><span className="text-sm font-black text-teal-main">{systemStats.total_users}</span></div></div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardAdmin;
